/**
 * Инструмент заливки (flood fill) слоя рисования гиперболической карты.
 *
 * Заливка — **растровая** операция (в отличие от векторных `stroke`/`shape`).
 * Согласно модели рисования (design.md, «Модель рисования») и Требованию 8.5,
 * заливка выполняется так:
 * 1. Текущий слой рисования **растеризуется** в пиксельный буфер фиксированного
 *    квадрата поверхности — ограничивающего прямоугольника единичного диска
 *    `[-1, 1] × [-1, 1]` — с разрешением, зависящим от уровня детализации
 *    (см. {@link resolutionForLevel}).
 * 2. От точки клика (переведённой из координат поверхности в пиксель буфера,
 *    см. {@link surfaceToMaskPixel}) выполняется **flood fill** связной области:
 *    4-связный обход заливает только ту замкнутую область, что содержит seed,
 *    и не «протекает» за замкнутые границы (Требование 8.5).
 * 3. Результат сохраняется как **растровая маска-регион** ({@link FloodMask}),
 *    закодированная в строку ({@link encodeMask}) и привязанная к уровню
 *    (`FillPrimitive.level`).
 *
 * Привязка к уровню и независимость от вида: маска рисуется поверх фиксированного
 * квадрата поверхности (bbox диска), поэтому соответствие «пиксель ↔ точка
 * поверхности» ({@link maskPixelToSurface}) полностью определяется размером маски
 * и не зависит от текущего вида `V`. Вид применяется к вершинам маски шейдером
 * рендерера при отрисовке — так заливка корректно отображается на всех уровнях
 * детализации; между уровнями разной чёткости маска переносится
 * передискретизацией ({@link resampleMask}).
 *
 * Ядро алгоритма (сам flood fill, кодирование/декодирование, передискретизация,
 * перевод координат, построение геометрии) — **чистые** функции над пиксельным
 * буфером/маской, тестируемые без DOM/WebGL. Растеризация слоя
 * ({@link rasterizeDrawingLayer}) требует 2D-контекста холста и потому вынесена
 * в тонкую обёртку, безопасную в не-браузерном окружении.
 *
 * Требования: 8.5
 */

import type { Complex, FillPrimitive, Mobius } from '@langton/shared';
import type { DrawingLayer } from './drawing-layer.js';
import { parseHexColor, type WebGLMapRenderer } from './webgl-map-renderer.js';

/** Цвет пикселя RGBA с компонентами в диапазоне `[0, 255]`. */
export interface PixelRgba {
  r: number;
  g: number;
  b: number;
  a: number;
}

/** Растровая маска-регион: битовая карта `0/1` заданного размера. */
export interface FloodMask {
  /** Ширина маски в пикселях (`> 0`). */
  width: number;
  /** Высота маски в пикселях (`> 0`). */
  height: number;
  /** Пиксели маски (`data[y * width + x]` — `1`, если пиксель залит). */
  data: Uint8Array;
}

/** Опции {@link floodFill}. */
export interface FloodFillOptions {
  /**
   * Допуск сравнения цветов (евклидово расстояние по каналам RGBA в `[0, 255]`).
   * Пиксель включается в область, если расстояние его цвета до цвета seed не
   * превышает `tolerance`. По умолчанию `0` (точное совпадение).
   */
  tolerance?: number;
  /**
   * Если `true`, дополнительно вернуть копию буфера с залитой областью
   * ({@link FloodFillResult.filled}). По умолчанию `false`.
   */
  produceFilledBuffer?: boolean;
}

/** Результат {@link floodFill}. */
export interface FloodFillResult {
  /** Маска-регион залитой связной области. */
  mask: FloodMask;
  /** Число залитых пикселей. */
  filledCount: number;
  /**
   * Копия исходного буфера с залитой областью (только если задано
   * {@link FloodFillOptions.produceFilledBuffer}); иначе `undefined`.
   */
  filled?: Uint8ClampedArray;
}

/** Базовое разрешение растеризации на нулевом уровне детализации. */
const BASE_RESOLUTION = 256;

/** Верхний предел стороны буфера растеризации (защита от чрезмерной памяти). */
const MAX_RESOLUTION = 4096;

/** Префикс-заголовок закодированной маски (версия формата). */
const MASK_HEADER = 'fmask:v1';

/**
 * Разрешение (сторона квадратного буфера) для растеризации на заданном уровне
 * детализации: `base · 2^level`, зажатое в `[base, MAX_RESOLUTION]`. Чем выше
 * уровень, тем чётче маска (Требование 8.5: привязка растровой заливки к уровню).
 *
 * @param level уровень детализации (`≥ 0`)
 * @param base базовое разрешение нулевого уровня (по умолчанию `256`)
 */
export function resolutionForLevel(level: number, base = BASE_RESOLUTION): number {
  const lvl = Number.isFinite(level) && level > 0 ? Math.floor(level) : 0;
  const size = base * Math.pow(2, lvl);
  return Math.max(base, Math.min(MAX_RESOLUTION, Math.floor(size)));
}

/**
 * Перевести точку поверхности в пиксель маски. Маска покрывает ограничивающий
 * прямоугольник единичного диска `[-1, 1] × [-1, 1]`; ось Y инвертируется
 * (пиксель `(0, 0)` — верхний левый угол). Результат может выходить за пределы
 * буфера, если точка вне bbox диска — вызывающая сторона обязана проверить.
 *
 * @param z точка поверхности (координаты диска)
 * @param width ширина маски в пикселях
 * @param height высота маски в пикселях
 * @returns целочисленные пиксельные координаты `(x, y)`
 */
export function surfaceToMaskPixel(
  z: Complex,
  width: number,
  height: number,
): { x: number; y: number } {
  const x = Math.floor(((z.re + 1) / 2) * width);
  const y = Math.floor(((1 - z.im) / 2) * height);
  return { x, y };
}

/**
 * Перевести пиксель маски (по его центру) в точку поверхности — обратно к
 * {@link surfaceToMaskPixel}. Используется при построении геометрии маски для
 * отрисовки (вершины в координатах поверхности; вид `V` применяется шейдером).
 *
 * @param px пиксельная координата X
 * @param py пиксельная координата Y
 * @param width ширина маски в пикселях
 * @param height высота маски в пикселях
 * @returns точка поверхности (координаты диска)
 */
export function maskPixelToSurface(
  px: number,
  py: number,
  width: number,
  height: number,
): Complex {
  return {
    re: ((px + 0.5) / width) * 2 - 1,
    im: 1 - ((py + 0.5) / height) * 2,
  };
}

/** Прочитать цвет пикселя из RGBA-буфера по координатам. */
function pixelAt(
  pixels: Uint8ClampedArray | Uint8Array,
  width: number,
  x: number,
  y: number,
): PixelRgba {
  const i = (y * width + x) * 4;
  return { r: pixels[i], g: pixels[i + 1], b: pixels[i + 2], a: pixels[i + 3] };
}

/** Квадрат евклидова расстояния между цветами RGBA. */
function colorDistanceSq(a: PixelRgba, b: PixelRgba): number {
  const dr = a.r - b.r;
  const dg = a.g - b.g;
  const db = a.b - b.b;
  const da = a.a - b.a;
  return dr * dr + dg * dg + db * db + da * da;
}

/**
 * Flood fill связной замкнутой области пиксельного буфера от точки `(seedX, seedY)`
 * (Требование 8.5).
 *
 * Заливается **только** та 4-связная область, что содержит seed и цвет которой
 * совпадает с цветом seed в пределах `tolerance`. Обход не пересекает пиксели,
 * чей цвет отличается сильнее допуска, — поэтому замкнутая граница (нарисованная
 * фигура/кисть) не даёт заливке «протечь» наружу. Если seed вне буфера, результат
 * — пустая маска.
 *
 * Алгоритм — итеративный 4-связный обход стеком (без рекурсии, устойчив к большим
 * областям). Чистая функция: исходный `pixels` не мутируется.
 *
 * @param pixels RGBA-буфер (`width · height · 4` байт, каналы `[0, 255]`)
 * @param width ширина буфера в пикселях (`> 0`)
 * @param height высота буфера в пикселях (`> 0`)
 * @param seedX X-координата точки старта
 * @param seedY Y-координата точки старта
 * @param options допуск сравнения цветов и флаг возврата залитого буфера
 * @returns маска-регион, число залитых пикселей и (опционально) залитый буфер
 */
export function floodFill(
  pixels: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
  seedX: number,
  seedY: number,
  options: FloodFillOptions = {},
): FloodFillResult {
  const w = Math.floor(width);
  const h = Math.floor(height);
  const mask: FloodMask = { width: w, height: h, data: new Uint8Array(w * h) };

  const sx = Math.floor(seedX);
  const sy = Math.floor(seedY);
  // Seed вне буфера — заливать нечего.
  if (w <= 0 || h <= 0 || sx < 0 || sy < 0 || sx >= w || sy >= h) {
    return { mask, filledCount: 0 };
  }

  const tolerance = Math.max(0, options.tolerance ?? 0);
  const toleranceSq = tolerance * tolerance;
  const target = pixelAt(pixels, w, sx, sy);

  let filledCount = 0;
  const stack: number[] = [sy * w + sx];
  const visited = mask.data;

  while (stack.length > 0) {
    const idx = stack.pop() as number;
    if (visited[idx] === 1) {
      continue;
    }
    const x = idx % w;
    const y = (idx - x) / w;
    if (colorDistanceSq(pixelAt(pixels, w, x, y), target) > toleranceSq) {
      continue;
    }
    visited[idx] = 1;
    filledCount++;
    if (x > 0) {
      stack.push(idx - 1);
    }
    if (x < w - 1) {
      stack.push(idx + 1);
    }
    if (y > 0) {
      stack.push(idx - w);
    }
    if (y < h - 1) {
      stack.push(idx + w);
    }
  }

  const result: FloodFillResult = { mask, filledCount };
  if (options.produceFilledBuffer) {
    // Предпросмотр: залитая область помечается непрозрачным чёрным (цвет заливки
    // здесь неизвестен — он применяется на этапе отрисовки примитива).
    result.filled = paintMask(pixels, mask, { r: 0, g: 0, b: 0, a: 255 });
  }
  return result;
}

/**
 * Наложить цвет заливки на маску поверх копии исходного буфера. Возвращает новый
 * буфер (исходный не мутируется). Вспомогательно для предпросмотра/тестов.
 *
 * @param pixels исходный RGBA-буфер
 * @param mask маска-регион
 * @param color цвет заливки (`[0, 255]`)
 */
export function paintMask(
  pixels: Uint8ClampedArray | Uint8Array,
  mask: FloodMask,
  color: PixelRgba,
): Uint8ClampedArray {
  const out = new Uint8ClampedArray(pixels);
  for (let i = 0; i < mask.data.length; i++) {
    if (mask.data[i] === 1) {
      const j = i * 4;
      out[j] = color.r;
      out[j + 1] = color.g;
      out[j + 2] = color.b;
      out[j + 3] = color.a;
    }
  }
  return out;
}

/**
 * Преобразовать hex-цвет в {@link PixelRgba} с компонентами `[0, 255]`
 * (переиспользует разбор {@link parseHexColor} рендерера).
 */
export function hexToPixel(hex: string): PixelRgba {
  const c = parseHexColor(hex);
  return {
    r: Math.round(c.r * 255),
    g: Math.round(c.g * 255),
    b: Math.round(c.b * 255),
    a: Math.round(c.a * 255),
  };
}

/**
 * Закодировать маску-регион в компактную строку для хранения в
 * `FillPrimitive.mask`. Формат — RLE по битам маски:
 * `fmask:v1:W:H:S:run0,run1,...`, где `W`/`H` — размеры, `S` — стартовое
 * значение (`0`/`1`), далее чередующиеся длины серий. Кодирование точное и
 * полностью обратимо ({@link decodeMask}), без потерь и без зависимости от
 * `Buffer`/`btoa` (работает и в Node, и в браузере).
 *
 * @param mask маска-регион
 * @returns строковое представление маски
 */
export function encodeMask(mask: FloodMask): string {
  const { width, height, data } = mask;
  const runs: number[] = [];
  const start = data.length > 0 ? data[0] : 0;
  let current = start;
  let count = 0;
  for (let i = 0; i < data.length; i++) {
    if (data[i] === current) {
      count++;
    } else {
      runs.push(count);
      current = data[i];
      count = 1;
    }
  }
  if (count > 0) {
    runs.push(count);
  }
  return `${MASK_HEADER}:${width}:${height}:${start}:${runs.join(',')}`;
}

/**
 * Декодировать маску-регион из строки {@link encodeMask}. Возвращает `null`, если
 * строка не соответствует формату или размеры некорректны.
 *
 * @param encoded строковое представление маски
 * @returns маска-регион либо `null`
 */
export function decodeMask(encoded: string): FloodMask | null {
  if (typeof encoded !== 'string' || !encoded.startsWith(`${MASK_HEADER}:`)) {
    return null;
  }
  // Разбиваем максимум на 5 частей: заголовок отбрасываем, дальше W:H:S:runs.
  const body = encoded.slice(MASK_HEADER.length + 1);
  const firstColon = body.indexOf(':');
  const secondColon = body.indexOf(':', firstColon + 1);
  const thirdColon = body.indexOf(':', secondColon + 1);
  if (firstColon < 0 || secondColon < 0 || thirdColon < 0) {
    return null;
  }
  const width = Number.parseInt(body.slice(0, firstColon), 10);
  const height = Number.parseInt(body.slice(firstColon + 1, secondColon), 10);
  const start = Number.parseInt(body.slice(secondColon + 1, thirdColon), 10);
  const runsPart = body.slice(thirdColon + 1);
  if (
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    width <= 0 ||
    height <= 0 ||
    (start !== 0 && start !== 1)
  ) {
    return null;
  }
  const data = new Uint8Array(width * height);
  let value = start;
  let pos = 0;
  if (runsPart.length > 0) {
    for (const token of runsPart.split(',')) {
      const run = Number.parseInt(token, 10);
      if (!Number.isFinite(run) || run < 0) {
        return null;
      }
      if (value === 1) {
        data.fill(1, pos, Math.min(pos + run, data.length));
      }
      pos += run;
      value = value === 0 ? 1 : 0;
    }
  }
  return { width, height, data };
}

/**
 * Передискретизировать маску-регион в новый размер методом ближайшего соседа.
 * Используется для переноса заливки между уровнями детализации разной чёткости
 * (design.md: растровая заливка переносится на другие уровни передискретизацией).
 *
 * @param mask исходная маска
 * @param targetWidth целевая ширина (`> 0`)
 * @param targetHeight целевая высота (`> 0`)
 * @returns маска нового размера
 */
export function resampleMask(
  mask: FloodMask,
  targetWidth: number,
  targetHeight: number,
): FloodMask {
  const tw = Math.max(1, Math.floor(targetWidth));
  const th = Math.max(1, Math.floor(targetHeight));
  if (tw === mask.width && th === mask.height) {
    return { width: tw, height: th, data: mask.data.slice() };
  }
  const data = new Uint8Array(tw * th);
  for (let y = 0; y < th; y++) {
    // Ближайший сосед по центру целевого пикселя.
    const sy = Math.min(
      mask.height - 1,
      Math.floor(((y + 0.5) / th) * mask.height),
    );
    for (let x = 0; x < tw; x++) {
      const sx = Math.min(
        mask.width - 1,
        Math.floor(((x + 0.5) / tw) * mask.width),
      );
      data[y * tw + x] = mask.data[sy * mask.width + sx];
    }
  }
  return { width: tw, height: th, data };
}

/** Горизонтальный залитый отрезок маски в строке `y` от `x0` до `x1` включительно. */
export interface MaskSpan {
  y: number;
  x0: number;
  x1: number;
}

/**
 * Разложить маску на горизонтальные залитые отрезки (по строкам). Слияние
 * соседних залитых пикселей в один отрезок сокращает число примитивов при
 * отрисовке маски.
 *
 * @param mask маска-регион
 * @returns массив отрезков (пустой, если маска пуста)
 */
export function maskToSpans(mask: FloodMask): MaskSpan[] {
  const { width, height, data } = mask;
  const spans: MaskSpan[] = [];
  for (let y = 0; y < height; y++) {
    let x = 0;
    while (x < width) {
      if (data[y * width + x] === 1) {
        const x0 = x;
        while (x < width && data[y * width + x] === 1) {
          x++;
        }
        spans.push({ y, x0, x1: x - 1 });
      } else {
        x++;
      }
    }
  }
  return spans;
}

/**
 * Построить вершины треугольников (в координатах поверхности) для отрисовки
 * маски-региона. Каждый горизонтальный отрезок маски ({@link maskToSpans})
 * превращается в прямоугольную ячейку поверхности (два треугольника), покрытую
 * заливкой. Возвращает плоский массив вершин в порядке для режима `'triangles'`
 * ({@link WebGLMapRenderer.drawVertices}); вид `V` применяется шейдером при
 * отрисовке. Углы ячеек берутся по границам пикселей ({@link maskPixelToSurface}
 * работает по центрам, поэтому здесь используется прямой пересчёт границ).
 *
 * @param mask маска-регион
 * @returns вершины треугольников в координатах поверхности
 */
export function maskToTriangles(mask: FloodMask): Complex[] {
  const { width, height } = mask;
  const spans = maskToSpans(mask);
  const tris: Complex[] = [];
  // Граница пикселя (без смещения на центр) → координата поверхности.
  const edgeX = (px: number): number => (px / width) * 2 - 1;
  const edgeY = (py: number): number => 1 - (py / height) * 2;
  for (const span of spans) {
    const left = edgeX(span.x0);
    const right = edgeX(span.x1 + 1);
    const top = edgeY(span.y);
    const bottom = edgeY(span.y + 1);
    const tl: Complex = { re: left, im: top };
    const tr: Complex = { re: right, im: top };
    const br: Complex = { re: right, im: bottom };
    const bl: Complex = { re: left, im: bottom };
    // Два треугольника прямоугольной ячейки.
    tris.push(tl, tr, br, tl, br, bl);
  }
  return tris;
}

/**
 * Собрать {@link FillPrimitive} из результата заливки: маска кодируется в строку
 * и привязывается к уровню (Требование 8.5).
 *
 * @param seed точка поверхности старта заливки
 * @param color цвет заливки (hex)
 * @param level уровень детализации растеризации
 * @param mask маска-регион залитой области
 */
export function createFillPrimitive(
  seed: Complex,
  color: string,
  level: number,
  mask: FloodMask,
): FillPrimitive {
  return {
    kind: 'fill',
    seed: { re: seed.re, im: seed.im },
    color,
    level,
    mask: encodeMask(mask),
  };
}

/**
 * Реализация хука {@link DrawingLayerOptions.renderFill}: декодирует маску
 * `FillPrimitive`, строит геометрию ячеек ({@link maskToTriangles}) и рисует её
 * цветом заливки через рендерер под текущим видом. Некорректная/пустая маска
 * пропускается. Опционально маска передискретизируется под целевое разрешение
 * (перенос между уровнями).
 *
 * @param options.targetLevel уровень, под разрешение которого передискретизировать
 *   маску перед отрисовкой (по умолчанию — разрешение хранимой маски)
 * @returns функция-хук для `DrawingLayer`
 */
export function createFloodFillRenderer(
  options: { targetLevel?: number } = {},
): (fill: FillPrimitive, renderer: WebGLMapRenderer, view: Mobius) => void {
  return (fill, renderer, view) => {
    const decoded = decodeMask(fill.mask);
    if (!decoded) {
      return;
    }
    let mask = decoded;
    if (options.targetLevel !== undefined) {
      const size = resolutionForLevel(options.targetLevel);
      mask = resampleMask(decoded, size, size);
    }
    const tris = maskToTriangles(mask);
    if (tris.length < 3) {
      return;
    }
    renderer.drawVertices(tris, 'triangles', fill.color, view);
  };
}

/**
 * Растеризовать векторные примитивы слоя рисования в RGBA-буфер квадрата
 * поверхности (bbox единичного диска) для последующего flood fill. Требует
 * 2D-контекста холста, поэтому возвращает `null` в не-браузерном окружении.
 *
 * Растеризация выполняется в координатах поверхности (без применения вида `V`),
 * что делает маску независимой от текущего вида. Фон буфера — прозрачный;
 * векторные примитивы рисуются своим цветом. Замкнутые границы фигур/кистей
 * ограничивают область заливки (Требование 8.5).
 *
 * @param layer слой рисования (источник векторных примитивов)
 * @param level уровень детализации (определяет разрешение буфера)
 * @param backgroundColor цвет фона буфера (по умолчанию прозрачный)
 * @returns буфер и его размеры либо `null`, если холст недоступен
 */
export function rasterizeDrawingLayer(
  layer: DrawingLayer,
  level: number,
  backgroundColor?: string,
): { pixels: Uint8ClampedArray; width: number; height: number } | null {
  if (typeof document === 'undefined') {
    return null;
  }
  const size = resolutionForLevel(level);
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return null;
  }
  if (backgroundColor) {
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, size, size);
  } else {
    ctx.clearRect(0, 0, size, size);
  }
  // Перевод точки поверхности в пиксель холста (bbox диска, с инверсией Y).
  const toPx = (z: Complex): { x: number; y: number } => ({
    x: ((z.re + 1) / 2) * size,
    y: ((1 - z.im) / 2) * size,
  });
  for (const primitive of layer.getAll()) {
    if (primitive.kind === 'fill') {
      continue;
    }
    for (const cmd of layer.buildCommands(primitive)) {
      if (cmd.vertices.length < 2) {
        continue;
      }
      ctx.strokeStyle = cmd.color;
      ctx.fillStyle = cmd.color;
      ctx.lineWidth = 1;
      ctx.beginPath();
      const first = toPx(cmd.vertices[0]);
      ctx.moveTo(first.x, first.y);
      for (let i = 1; i < cmd.vertices.length; i++) {
        const p = toPx(cmd.vertices[i]);
        ctx.lineTo(p.x, p.y);
      }
      if (cmd.mode === 'triangles' || cmd.mode === 'line-loop') {
        ctx.closePath();
      }
      if (cmd.mode === 'triangles') {
        ctx.fill();
      }
      ctx.stroke();
    }
  }
  const image = ctx.getImageData(0, 0, size, size);
  return { pixels: image.data, width: size, height: size };
}

/**
 * Высокоуровневая заливка слоя от точки поверхности: растеризует слой, переводит
 * seed в пиксель, выполняет flood fill и собирает {@link FillPrimitive}
 * (Требование 8.5). Требует 2D-контекста холста; возвращает `null`, если холст
 * недоступен или seed попал вне буфера / в уже «граничный» пиксель без области.
 *
 * @param layer слой рисования
 * @param seed точка поверхности (клик инструментом заливки)
 * @param color цвет заливки (hex)
 * @param level уровень детализации
 * @param options допуск сравнения цветов и т.п.
 * @returns готовый `FillPrimitive` либо `null`
 */
export function buildFillPrimitiveFromLayer(
  layer: DrawingLayer,
  seed: Complex,
  color: string,
  level: number,
  options: FloodFillOptions = {},
): FillPrimitive | null {
  const raster = rasterizeDrawingLayer(layer, level);
  if (!raster) {
    return null;
  }
  const { x, y } = surfaceToMaskPixel(seed, raster.width, raster.height);
  const result = floodFill(raster.pixels, raster.width, raster.height, x, y, options);
  if (result.filledCount === 0) {
    return null;
  }
  return createFillPrimitive(seed, color, level, result.mask);
}
